/**
 * What YouTube requires of an uploader: the host sets the title and description, chooses who can
 * see the video, and sees which channel it goes to before anything is uploaded.
 */
import type { YouTubeOptions } from '@str/shared';

type Props = {
  accountName: string;
  title: string;
  onTitle: (next: string) => void;
  /** The words as they will be posted; the host may change them here. */
  words: string;
  onWords: (next: string) => void;
  /** Saves the words once the host leaves the field, like the composer's caption editor. */
  onWordsCommit?: () => void;
  privacy: YouTubeOptions['privacyStatus'];
  onPrivacy: (next: YouTubeOptions['privacyStatus']) => void;
};

const AUDIENCES: { value: YouTubeOptions['privacyStatus']; label: string; note: string }[] = [
  { value: 'public', label: 'Public', note: 'Anyone can find and watch it' },
  { value: 'unlisted', label: 'Unlisted', note: 'Only people with the link' },
  { value: 'private', label: 'Private', note: 'Only you' },
];

const box: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  padding: '14px 16px',
  borderRadius: 14,
  border: '1px solid rgba(0,0,0,.1)',
  background: '#faf8f4',
};
const field: React.CSSProperties = {
  font: 'inherit',
  fontSize: 13,
  lineHeight: 1.45,
  padding: '8px 10px',
  borderRadius: 10,
  border: '1px solid rgba(0,0,0,.15)',
  background: '#fff',
  width: '100%',
};
const stack: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4 };
const note: React.CSSProperties = { fontSize: 11.5, color: '#666', lineHeight: 1.45 };

export function YouTubePost({
  accountName,
  title,
  onTitle,
  words,
  onWords,
  onWordsCommit,
  privacy,
  onPrivacy,
}: Props) {
  return (
    <div style={box} role="group" aria-label={`Uploading to YouTube as ${accountName}`}>
      <span style={{ fontSize: 13.5, fontWeight: 700 }}>Uploading to YouTube as {accountName}</span>
      <label style={stack}>
        <span className="label-caps">Title</span>
        <input
          aria-label="Title"
          value={title}
          maxLength={100}
          onChange={(e) => onTitle(e.target.value)}
          style={field}
        />
      </label>
      <label style={stack}>
        <span className="label-caps">Description</span>
        <textarea
          aria-label="Description"
          value={words}
          rows={3}
          onChange={(e) => onWords(e.target.value)}
          onBlur={() => onWordsCommit?.()}
          style={{ ...field, resize: 'vertical' }}
        />
      </label>
      <fieldset style={{ ...stack, border: 0, padding: 0, margin: 0 }}>
        <legend className="label-caps" style={{ padding: 0, marginBottom: 4 }}>
          Who can see it
        </legend>
        {AUDIENCES.map((a) => (
          <label
            key={a.value}
            style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}
          >
            <input
              type="radio"
              name="youtube-privacy"
              checked={privacy === a.value}
              onChange={() => onPrivacy(a.value)}
            />
            <span>
              <strong>{a.label}</strong> · {a.note}
            </span>
          </label>
        ))}
      </fieldset>
      <span style={note}>
        Marked as not made for kids. A video made with AI is declared to YouTube, which shows its
        altered or synthetic content label.
      </span>
    </div>
  );
}
